import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';

import {forkJoin} from 'rxjs';
import {finalize, map, tap} from 'rxjs/operators';

import {RegulationTreeService} from '../../../+configuration/regulation-tree/regulation-tree.service';
import {PayEventsService} from '../../../+payrolls';
import {CliReg} from '../../../models/models.interfaces';
import {Data} from '../../api';
import {
  DefaultValue,
  FormCardFieldComponent
} from '../../form-generator/form-card/form-card-field/form-card-field.component';
import {GroupZoneDescriptor, ZoneDescriptor} from '../../form-generator/form-generator.interfaces';
import {FormGeneratorUtils} from '../../form-generator/form-generator.utils';
import {TreeNode} from '../../ui';
import {isPresent} from '../../utils/lang';
import {HireUtilsService} from '../hire-utils.service';
import {FormDescriptorService} from '../../form-generator/form-descriptor.service';
import {ResponseStatusCodes} from '../../../app.constants';

@Component({
  selector: 'gp-hire-additional-contract-information',
  templateUrl: './hire-additional-information.component.html'
})
export class HireAdditionalInformationComponent implements OnInit, OnChanges {

  @ViewChild('fieldRowsContainer', {read: ViewContainerRef, static: true})
  public rowsContainer: ViewContainerRef;

  @Input()
  public mssHireEnabled = false;

  @Input()
  public regulationForm: UntypedFormGroup;

  @Input()
  public paySerieId: number;

  @Input()
  public regulationReadOnly = false;

  @Output()
  fieldsChange = new EventEmitter<Array<FormCardFieldComponent>>();

  @Output()
  regulationChange = new EventEmitter<{ cliRegId: number, cliRegNickname: string }>();

  public loadingRegulations: boolean = false;
  public regulations: Array<Data<CliReg>> = null;
  public regulationTree: Array<TreeNode<CliReg>> = null;
  public regulationFields: ComponentRef<FormCardFieldComponent>[] = [];
  public loadingPaySerie: boolean = true;

  private additionalZoneDescriptors: ZoneDescriptor[] = [];

  constructor(private formBuilder: UntypedFormBuilder,
              private componentFactoryResolver: ComponentFactoryResolver,
              private changeDetectorRef: ChangeDetectorRef,
              private payEventsService: PayEventsService,
              private formDescriptorService: FormDescriptorService,
              private hireUtilsService: HireUtilsService,
              private regulationTreeService: RegulationTreeService) {
  }

  ngOnInit() {
    this.regulationForm.addControl('regulation', this.formBuilder.control('', Validators.required));
    this.regulationForm.setValidators((group: UntypedFormGroup) => Object.keys(group.controls).length > 1 ? null : {addInfoEmpty: true});
  }

  ngOnChanges() {
    this.resetRegulations();
    if (!this.paySerieId) {
      return;
    }
    this.loadingPaySerie = true;
    this.loadingRegulations = true;
    this.payEventsService.getCliRegList(this.paySerieId)
      .pipe(finalize(() => {
        this.loadingPaySerie = false;
        this.loadingRegulations = false;
        this.clearRegulationForm(true);
      }))
      .subscribe((response: { data: Array<Data<CliReg>>, transId: string }) => {
        this.regulations = response.data;
        this.regulationTree = this.regulationTreeService.createTree(this.regulations);
      });
  }

  public regulationSelected(cliRegId: number) {
    if (!cliRegId) {
      this.clearRegulationForm(false);
      this.regulationChange.emit({cliRegId: null, cliRegNickname: null});
      return;
    }
    this.getFormDescriptor(cliRegId);
    this.regulationChange.emit({cliRegId: cliRegId, cliRegNickname: this.selectedRegulationNickName});
  }

  public resetRegulations() {
    this.regulationTree = [];
    this.regulations = null;
  }

  public get selectedRegulationNickName(): string {
    const regulation = this.regulationForm.get('regulation').value;

    if (!isPresent(regulation) || !isPresent(this.regulations)) {
      return '';
    }
    const selected: Data<CliReg> = this.regulations.find((r: Data<CliReg>) => r.id === regulation);

    return selected ? selected.attributes.nickName : '';
  }

  public summary(): { zoneName: string, zoneTransId: string, zoneValue: string }[] {
    const summary = [];
    for (const additionalZoneDescriptor of this.additionalZoneDescriptors) {
      if (additionalZoneDescriptor.zoneID === 'PayrollSets') {
        continue;
      }
      const value = this.regulationForm
        ?.get(FormGeneratorUtils.sanitizeZoneName(additionalZoneDescriptor.zoneID))
        ?.value;
      if (value) {
        const valueData = this.hireUtilsService.getValueData(additionalZoneDescriptor, value);
        summary.push({
          zoneName: additionalZoneDescriptor.zoneID,
          zoneTransId: additionalZoneDescriptor.transId,
          zoneValue: valueData.transId || valueData.value + ''
        });
      }
    }
    return summary;
  }

  private clearRegulationForm(reset: boolean = false) {
    this.regulationFields.forEach(ref => {
      this.regulationForm.removeControl(FormGeneratorUtils.sanitizeZoneName(ref.instance.descriptor.zoneID));
      ref.destroy();
    });
    this.regulationFields = [];
    this.additionalZoneDescriptors = [];
    if (reset) {
      this.regulationForm.reset();
    }
  }

  private getFormDescriptor(cliRegId: number) {
    if (!cliRegId) {
      return;
    }

    const confidentiality$ = this.formDescriptorService.getConfidentialityFields(cliRegId).pipe(
      tap(response => {
        this.updateRegulationFormValidators(response.statusCode, response.result.length > 0);
      }),
      map(response => response.result)
    );

    const workflowFields$ = this.mssHireEnabled
      ? this.formDescriptorService.getWorkflowFields(cliRegId, 'MSS_HIRE')
      : this.formDescriptorService.getWorkflowFields(cliRegId, 'PEOPLE_HIRE');

    forkJoin([confidentiality$, workflowFields$]).pipe(
      tap(([confidentialityFields, workflowFields]) => {
        const data: ZoneDescriptor[] = confidentialityFields.concat(workflowFields);
        this.renderFormCardFields(data);

        this.regulationForm.updateValueAndValidity();
      })
    ).subscribe();
  }

  private updateRegulationFormValidators(statusCode: number, hasResults: boolean) {
    if (statusCode === ResponseStatusCodes.NoContent || (statusCode === ResponseStatusCodes.OK && hasResults)) {
      this.regulationForm.setValidators(((group: UntypedFormGroup) =>
        Object.keys(group.controls).length > 1 ? null : { addInfoEmpty: true }
      ) as ValidatorFn);
    } else if (statusCode === ResponseStatusCodes.OK && !hasResults) {
      this.regulationForm.setValidators(null);
    }
  }


  private renderFormCardFields(zoneDescriptors: ZoneDescriptor[]) {
    this.clearRegulationForm();

    const promisesOfLoadedObservables: Array<Promise<FormCardFieldComponent>> =
      zoneDescriptors.map((zoneDescriptor: ZoneDescriptor) => {
        if (zoneDescriptor.metadatas && zoneDescriptor.metadatas.items) {
          zoneDescriptor.metadatas.items.forEach((item: { display: string, value: string }) => {
            if (!isPresent(item.display)) {
              item.display = item.value;
            }
          });
        }

        const fieldRef: ComponentRef<FormCardFieldComponent> = this.rowsContainer.createComponent(
          this.componentFactoryResolver.resolveComponentFactory(FormCardFieldComponent));

        this.changeDetectorRef.markForCheck();

        const field: FormCardFieldComponent = fieldRef.instance;
        this.regulationFields.push(fieldRef);
        this.additionalZoneDescriptors.push(zoneDescriptor);
        field.groupZoneDescriptor = <GroupZoneDescriptor>{zones: []};
        field.descriptor = zoneDescriptor;
        field.value = new DefaultValue();
        field.readOnly = zoneDescriptor.readOnly;
        field.editMode = true;

        return field.loaded.toPromise();
      });

    Promise.all(promisesOfLoadedObservables).then((fields: FormCardFieldComponent[]) => {
      this.fieldsChange.emit(fields);
      fields.forEach((item: FormCardFieldComponent) =>
        this.regulationForm.addControl(FormGeneratorUtils.sanitizeZoneName(item.descriptor.zoneID), item.control));
    });
  }
}
